import * as Yup from 'yup';

import {
  coInsurersSchema,
  isCoInsurerSplitValid,
} from '../utils/coInsurerSchema';
import { getCowbellTranslations } from '../../../i18n/translations';
import type { LayerKind } from '../utils/layerFieldSets';

// Human-friendly field labels for validation messages; the schema keys
// ("policyNumber", "retention") are not what an underwriter should read.
const FIELD_LABELS: Record<string, string> = {
  carrier: 'Carrier',
  policyNumber: 'Policy Number',
  limit: 'Limit',
  premium: 'Premium',
  percentage: 'Share',
};

// The primary layer's retention is region-aware ("Retention" in the US,
// "Excess" in the UK/AU), so it resolves through i18n rather than a fixed label.
const labelFor = (key: string): string => {
  if (key === 'retention') {
    return getCowbellTranslations().t('retention');
  }
  return FIELD_LABELS[key] ?? key;
};

/**
 * Standalone tower-schema → Yup adapter (FPX-5871).
 *
 * Converts the `excessTower` JSON Schema (well-formedness contract, FPX-5829/
 * 5853) into per-layer Yup object schemas that validate the **flat** layer form
 * model the `<Tower>` edit modal drives. This adapter is intentionally separate
 * from the shared `form-engine` `generateYupSchemaFromJSONSchema`: it is not
 * imported or modified here.
 *
 * The co-insurer Σ=100 rule (FPX-5832/5864) is a cross-field array sum that JSON
 * Schema cannot express, so it is composed in from `coInsurersSchema`.
 */

type JsonType = string | readonly string[];

interface ValueConstraints {
  type: JsonType;
  exclusiveMinimum?: number;
  minimum?: number;
  maximum?: number;
}

interface FieldSchemaNode {
  type?: JsonType;
  required?: readonly string[];
  properties?: {
    value?: ValueConstraints;
    values?: { items: { properties: { props: LayerNode } } };
    [key: string]: unknown;
  };
}

interface LayerNode {
  type?: JsonType;
  required?: readonly string[];
  properties: Record<string, FieldSchemaNode>;
}

interface LayerWrapperNode {
  type?: JsonType;
  required?: readonly string[];
  properties: { props: LayerNode };
}

interface UnderlyingWrapperNode {
  type?: JsonType;
  properties: {
    values: {
      type?: JsonType;
      minItems?: number;
      items: LayerWrapperNode;
    };
  };
}

export interface TowerJsonSchema {
  type?: JsonType;
  required?: readonly string[];
  properties: {
    props: {
      type?: JsonType;
      required?: readonly string[];
      properties: {
        primary: LayerWrapperNode;
        underlying?: UnderlyingWrapperNode;
        cowbellAttachment: LayerWrapperNode;
      };
    };
  };
}

export type TowerLayerSchemas = Record<LayerKind, Yup.ObjectSchema<any>>;

const CO_INSURERS_KEY = 'coInsurers';
const LAYER_DOC_KEY = 'layerPdfDocId';

const includesType = (type: JsonType | undefined, target: string): boolean =>
  Array.isArray(type) ? type.includes(target) : type === target;

const buildNumberField = (
  key: string,
  value: ValueConstraints | undefined,
  required: boolean
): Yup.NumberSchema => {
  const label = labelFor(key);
  let field = Yup.number()
    .nullable()
    .transform((parsed, original) => (original === '' ? null : parsed));

  if (typeof value?.exclusiveMinimum === 'number') {
    field = field.moreThan(
      value.exclusiveMinimum,
      `${label} must be greater than ${value.exclusiveMinimum}`
    );
  }
  if (typeof value?.minimum === 'number') {
    field = field.min(
      value.minimum,
      `${label} must be at least ${value.minimum}`
    );
  }
  if (typeof value?.maximum === 'number') {
    field = field.max(
      value.maximum,
      `${label} must be at most ${value.maximum}`
    );
  }
  if (required) {
    field = field.required(`${label} is required`);
  }
  return field;
};

const buildStringField = (key: string, required: boolean): Yup.StringSchema =>
  required
    ? Yup.string().required(`${labelFor(key)} is required`)
    : Yup.string().nullable();

const buildScalarField = (
  key: string,
  node: FieldSchemaNode,
  required: boolean
): Yup.Schema<any> =>
  includesType(node.properties?.value?.type, 'number')
    ? buildNumberField(key, node.properties?.value, required)
    : buildStringField(key, required);

const buildCoInsurersField = (node: FieldSchemaNode): Yup.Schema<any> => {
  const rowProps = node.properties?.values?.items?.properties?.props;
  if (!rowProps) {
    return coInsurersSchema;
  }

  const required = new Set(rowProps.required ?? []);
  const rowShape = Object.entries(rowProps.properties).reduce<
    Record<string, Yup.Schema<any>>
  >((acc, [key, fieldNode]) => {
    acc[key] = buildScalarField(key, fieldNode, required.has(key));
    return acc;
  }, {});

  return Yup.array()
    .of(Yup.object(rowShape))
    .test(
      'lead-keeps-remainder',
      'Co-insurer shares must leave the primary carrier above 0%.',
      (rows) => !rows?.length || isCoInsurerSplitValid(rows)
    );
};

const buildFieldSchema = (
  key: string,
  node: FieldSchemaNode,
  required: boolean
): Yup.Schema<any> => {
  if (key === CO_INSURERS_KEY) {
    return buildCoInsurersField(node);
  }
  if (key === LAYER_DOC_KEY) {
    return Yup.string().nullable();
  }
  return buildScalarField(key, node, required);
};

const buildLayerSchema = (layer?: LayerNode): Yup.ObjectSchema<any> => {
  if (!layer) {
    return Yup.object({});
  }

  const required = new Set(layer.required ?? []);
  const shape = Object.entries(layer.properties).reduce<
    Record<string, Yup.Schema<any>>
  >((acc, [key, node]) => {
    acc[key] = buildFieldSchema(key, node, required.has(key));
    return acc;
  }, {});

  return Yup.object(shape);
};

export function towerSchemaToYup(schema: TowerJsonSchema): TowerLayerSchemas {
  const { primary, underlying, cowbellAttachment } =
    schema.properties.props.properties;

  return {
    primary: buildLayerSchema(primary.properties.props),
    underlying: buildLayerSchema(
      underlying?.properties.values.items.properties.props
    ),
    cowbell: buildLayerSchema(cowbellAttachment.properties.props),
  };
}
